<template>
  <b-sidebar
    id="role-certificate-requirements"
    sidebar-class="sidebar-lg"
    :visible="sidebar.visible"
    bg-variant="white"
    shadow
    backdrop
    no-header
    right
    @shown="onShow"
    @change="val => mutateSidebar({ visible: val })"
  >
    <template #default="{ hide }">

      <!-- HEADER -->
      <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
        <h4>Certificado por nível</h4>

        <feather-icon
          class="cursor-pointer"
          icon="XIcon"
          size="16"
          @click="hide"
        />
      </div>

      <!-- LOADING -->
      <div
        v-if="loading"
        class="text-center py-3"
      >
        <b-spinner variant="primary" />
      </div>

      <!-- CONTENT -->
      <div
        v-else
        class="p-2"
      >
        <p class="text-muted">
          Selecione o certificado que o PN precisa ter aprovado para ser promovido a cada nível. Deixe em branco para não exigir.
        </p>

        <b-form-group
          v-for="role in roles"
          :key="role.consultant_role_id"
          :label="role.consultant_role_name"
          :label-for="`role-requirement-${role.consultant_role_id}`"
          class="mt-1"
        >
          <v-select
            :id="`role-requirement-${role.consultant_role_id}`"
            v-model="selected[role.consultant_role_id]"
            :options="certificateTypeOptions"
            :reduce="t => t.id"
            label="name"
            placeholder="Sem exigência"
            clearable
          />
        </b-form-group>

        <!-- AÇÕES -->
        <div class="d-flex mt-2 justify-content-end">
          <b-button
            variant="primary"
            class="certificate-buttons"
            :disabled="saving || !changedRequirements.length"
            @click="save"
          >
            {{ saving ? "Salvando..." : "Salvar" }}
          </b-button>
        </div>
      </div>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar,
  BFormGroup,
  BButton,
  BSpinner,
} from "bootstrap-vue";

import vSelect from "vue-select";
import { mapGetters, mapActions, mapMutations } from "vuex";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent";
import { getApiErrorMessage } from "@/helpers/error_messages";
import * as types from "../store/types";

export default {
  components: {
    BSidebar,
    BFormGroup,
    BButton,
    BSpinner,
    vSelect,
  },

  setup() {
    return {
      toast: useToast(),
    };
  },

  data() {
    return {
      loading: false,
      saving: false,
      roles: [],
      certificateTypes: [],
      selected: {},
      original: {},
    };
  },

  computed: {
    ...mapGetters({
      sidebar: types.ROLE_CERTIFICATE_REQUIREMENTS_SIDEBAR,
    }),

    certificateTypeOptions() {
      return this.certificateTypes.map(type => ({
        id: type.id,
        name: type.is_active ? type.name : `${type.name} (inativo)`,
      }));
    },

    changedRequirements() {
      return this.roles
        .filter(role => this.selected[role.consultant_role_id] !== this.original[role.consultant_role_id])
        .map(role => ({
          consultant_role_id: role.consultant_role_id,
          certificate_type_id: this.selected[role.consultant_role_id],
        }));
    },
  },

  methods: {
    ...mapMutations({
      mutateSidebar: types.MUTATE_ROLE_CERTIFICATE_REQUIREMENTS_SIDEBAR,
    }),

    ...mapActions({
      getRequirements: types.GET_ROLE_CERTIFICATE_REQUIREMENTS,
      saveRequirements: types.SAVE_ROLE_CERTIFICATE_REQUIREMENTS,
    }),

    async onShow() {
      this.loading = true;
      this.setRequirements({});

      try {
        const resp = await this.getRequirements();
        this.setRequirements(resp.data);
      } catch (error) {
        this.toast({
          component: ToastificationContent,
          props: {
            title: "Erro!",
            text: getApiErrorMessage(error, "Erro ao carregar os certificados por nível."),
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        this.loading = false;
      }
    },

    setRequirements(data) {
      const roles = (data && data.roles) || [];
      const selected = {};
      roles.forEach(role => {
        selected[role.consultant_role_id] = role.certificate_type_id || null;
      });

      this.roles = roles;
      this.certificateTypes = (data && data.certificate_types) || [];
      this.selected = selected;
      this.original = { ...selected };
    },

    async save() {
      if (!this.changedRequirements.length) return;
      this.saving = true;

      try {
        const resp = await this.saveRequirements({
          requirements: this.changedRequirements,
        });

        this.toast({
          component: ToastificationContent,
          props: {
            title: "Sucesso!",
            text: "Salvo com sucesso!",
            icon: "CheckIcon",
            variant: "success",
          },
        });
        this.setRequirements(resp.data);
      } catch (error) {
        this.toast({
          component: ToastificationContent,
          props: {
            title: "Erro!",
            text: getApiErrorMessage(error, "Erro ao salvar os certificados por nível."),
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.certificate-buttons {
  width: 11rem;
}
</style>
